import type {
  ClassroomView,
  FamilyDataActionInput,
  FamilyDataCorrectionInput,
  FamilyDataRequestReceipt,
  GuardianLoginInput,
  InviteLoginInput,
  LearningTrialStartInput,
  LearningTrialView,
  LessonDetailView,
  MathosWebApi,
  StudentAnswerPayload,
  SubmitAnswerResult,
} from "./contracts";
import { assertClientClassroomLesson } from "./curriculum-publication";

interface HttpAdapterOptions {
  allowNonReleaseSourceDrafts?: boolean;
}

async function request<T>(
  baseUrl: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    ...init,
  });

  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => undefined)) as { message?: unknown } | undefined;
    const serverMessage =
      typeof errorBody?.message === "string" ? errorBody.message : undefined;
    throw new Error(
      response.status === 401 && serverMessage === undefined
        ? "家庭登录已失效，请重新进入。"
        : serverMessage ?? "这一轮课堂暂时没有准备好，请稍后重试。",
    );
  }

  return (await response.json()) as T;
}

async function requestLessonDetail(
  baseUrl: string,
  lessonId: string,
  options: HttpAdapterOptions,
): Promise<LessonDetailView> {
  const detail = await request<LessonDetailView>(
    baseUrl,
    `/v2/lessons/${encodeURIComponent(lessonId)}`,
  );
  assertClientClassroomLesson(detail.lesson, {
    allowNonReleaseSourceDrafts:
      options.allowNonReleaseSourceDrafts === true,
  });
  return detail;
}

async function requestClassroom(
  baseUrl: string,
  path: string,
  options: HttpAdapterOptions,
  init?: RequestInit,
): Promise<ClassroomView> {
  const response = await request<ClassroomView>(baseUrl, path, init);
  const classroom = resolveVisualAssetUrls(response, baseUrl);
  assertClientClassroomLesson(classroom.lesson, {
    allowNonReleaseSourceDrafts:
      options.allowNonReleaseSourceDrafts === true,
  });
  return classroom;
}

async function requestAnswerResult(
  baseUrl: string,
  sessionId: string,
  answer: StudentAnswerPayload,
  options: HttpAdapterOptions,
): Promise<SubmitAnswerResult> {
  const response = await request<SubmitAnswerResult>(
    baseUrl,
    `/v2/classroom/${encodeURIComponent(sessionId)}/answers`,
    {
      method: "POST",
      body: JSON.stringify(answer),
    },
  );
  const result = {
    ...response,
    classroom: resolveVisualAssetUrls(response.classroom, baseUrl),
  };
  assertClientClassroomLesson(result.classroom.lesson, {
    allowNonReleaseSourceDrafts:
      options.allowNonReleaseSourceDrafts === true,
  });
  return result;
}

export function resolveVisualAssetUrls(
  classroom: ClassroomView,
  baseUrl: string,
): ClassroomView {
  if (!classroom.visualBoard) return classroom;
  return {
    ...classroom,
    visualBoard: {
      ...classroom.visualBoard,
      materials: classroom.visualBoard.materials.map((entry) =>
        entry.material.kind !== "source_crop"
          ? entry
          : {
              ...entry,
              material: {
                ...entry.material,
                assetUrl: new URL(entry.material.assetUrl, `${baseUrl}/`).toString(),
              },
            },
      ),
    },
  };
}

async function requestFamilyDataExport(
  baseUrl: string,
  input: FamilyDataActionInput,
): Promise<FamilyDataRequestReceipt> {
  const result = await request<
    FamilyDataRequestReceipt & { data?: unknown }
  >(baseUrl, "/v2/parent/data-export-requests", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const { data, ...receipt } = result;
  if (data !== undefined && typeof document !== "undefined") {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `mathos-family-data-${result.submittedAt.slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }
  return receipt;
}

export function createHttpAdapter(
  baseUrl: string,
  options: HttpAdapterOptions = {},
): MathosWebApi {
  return {
    getSession: () => request(baseUrl, "/v2/auth/session"),
    acceptInvite: (input: InviteLoginInput) =>
      request(baseUrl, "/v2/auth/invitations/accept", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    signIn: (input: GuardianLoginInput) =>
      request(baseUrl, "/v2/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    signOut: () =>
      request(baseUrl, "/v2/auth/sign-out", { method: "POST" }),
    getDashboard: () => request(baseUrl, "/v2/dashboard"),
    getLesson: (lessonId: string) =>
      requestLessonDetail(baseUrl, lessonId, options),
    startLesson: (lessonId: string) =>
      requestClassroom(
        baseUrl,
        `/v2/lessons/${encodeURIComponent(lessonId)}/sessions`,
        options,
        { method: "POST" },
      ),
    getClassroom: (sessionId: string) =>
      requestClassroom(
        baseUrl,
        `/v2/classroom/${encodeURIComponent(sessionId)}`,
        options,
      ),
    submitAnswer: (sessionId: string, answer: StudentAnswerPayload) =>
      requestAnswerResult(baseUrl, sessionId, answer, options),
    pauseSession: (sessionId: string) =>
      requestClassroom(
        baseUrl,
        `/v2/classroom/${encodeURIComponent(sessionId)}/pause`,
        options,
        { method: "POST" },
      ),
    resumeSession: (sessionId: string) =>
      requestClassroom(
        baseUrl,
        `/v2/classroom/${encodeURIComponent(sessionId)}/resume`,
        options,
        { method: "POST" },
      ),
    getParentEvidence: (lessonId: string) =>
      request(
        baseUrl,
        `/v2/parent/evidence/${encodeURIComponent(lessonId)}`,
      ),
    getRetention: (taskId: string) =>
      request(baseUrl, `/v2/retention/${encodeURIComponent(taskId)}`),
    submitRetention: (taskId: string, answer: StudentAnswerPayload) =>
      request(
        baseUrl,
        `/v2/retention/${encodeURIComponent(taskId)}/answers`,
        {
          method: "POST",
          body: JSON.stringify(answer),
        },
      ),
    getFamilyDataControls: () =>
      request(baseUrl, "/v2/parent/data-controls"),
    requestFamilyDataCorrection: (input: FamilyDataCorrectionInput) => {
      const { childId, ...body } = input;
      return request(
        baseUrl,
        `/v2/parent/children/${encodeURIComponent(childId)}/profile-corrections`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    requestFamilyDataExport: (input) =>
      requestFamilyDataExport(baseUrl, input),
    requestFamilyDataDeletion: (input) =>
      request(baseUrl, "/v2/parent/data-deletion-requests", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    getLearningTrial: (childId: string) =>
      request<LearningTrialView>(
        baseUrl,
        `/v2/parent/children/${encodeURIComponent(childId)}/learning-trial`,
      ),
    startLearningTrial: (input: LearningTrialStartInput) => {
      const { childId, ...body } = input;
      return request<LearningTrialView>(
        baseUrl,
        `/v2/parent/children/${encodeURIComponent(childId)}/learning-trial`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
  };
}
